import React, { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert";
import { Plus, Trash2, Edit, Image, X } from "lucide-react";
import { DOMAIN } from "../../strings/CONSTANTS";

const SurpriseCategories: React.FC = () => {
  const [categories, setCategories] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<any>(null);

  // Form state
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [imageBase64, setImageBase64] = useState("");
  const [imagePreview, setImagePreview] = useState("");
  const [order, setOrder] = useState(0);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${DOMAIN}/surprise/category/list`);
      if (res.data.success) {
        setCategories(res.data.data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
    if (!validTypes.includes(file.type)) {
      Swal("Invalid Format", "Please upload PNG, JPG, or WEBP image", "error");
      return;
    }

    const maxSize = 200 * 1024;
    if (file.size > maxSize) {
      Swal(
        "File Too Large",
        `Image size ${(file.size / 1024).toFixed(0)}KB. Max 200KB`,
        "error"
      );
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result?.toString().split(",")[1] || "";
      setImageBase64(base64);
      setImagePreview(reader.result?.toString() || "");
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      Swal("Error", "Category name is required", "error");
      return;
    }
    if (!imageBase64 && !editingCategory) {
      Swal("Error", "Category image is required", "error");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: name.trim(),
        description,
        imageBase64: imageBase64 || editingCategory?.imageBase64,
        order,
      };

      let res;
      if (editingCategory) {
        res = await axios.put(
          `${DOMAIN}/surprise/category/update/${editingCategory._id}`,
          payload
        );
      } else {
        res = await axios.post(`${DOMAIN}/surprise/category/create`, payload);
      }

      if (res.data.success) {
        Swal(
          "Success",
          editingCategory ? "Category updated" : "Category created",
          "success"
        );
        resetForm();
        fetchCategories();
      }
    } catch (err: any) {
      Swal(
        "Error",
        err.response?.data?.message || "Something went wrong",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    const result = await Swal({
      title: "Delete Category?",
      text: `Are you sure you want to delete "${name}"? All content in this category will also be deleted.`,
      icon: "warning",
      buttons: ["Cancel", "Delete"],
      dangerMode: true,
    });

    if (result) {
      try {
        const res = await axios.delete(
          `${DOMAIN}/surprise/category/delete/${id}`
        );
        if (res.data.success) {
          Swal("Deleted!", "Category deleted successfully", "success");
          fetchCategories();
        }
      } catch (err: any) {
        Swal(
          "Error",
          err.response?.data?.message || "Cannot delete category",
          "error"
        );
      }
    }
  };

  const resetForm = () => {
    setShowModal(false);
    setEditingCategory(null);
    setName("");
    setDescription("");
    setImageBase64("");
    setImagePreview("");
    setOrder(0);
  };

  const openEditModal = (category: any) => {
    setEditingCategory(category);
    setName(category.name);
    setDescription(category.description || "");
    setImagePreview(category.image);
    setOrder(category.order || 0);
    setShowModal(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-800">
              Surprise QR Categories
            </h1>
            <p className="text-slate-600 mt-1">
              Manage categories for surprise QR content
            </p>
          </div>
          <button
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Add Category
          </button>
        </div>

        <div className="bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left py-3 px-4 text-slate-600 font-semibold">
                  Image
                </th>
                <th className="text-left py-3 px-4 text-slate-600 font-semibold">
                  Name
                </th>
                <th className="text-left py-3 px-4 text-slate-600 font-semibold">
                  Description
                </th>
                <th className="text-left py-3 px-4 text-slate-600 font-semibold">
                  Order
                </th>
                <th className="text-left py-3 px-4 text-slate-600 font-semibold">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={5} className="text-center py-8 text-slate-500">
                    Loading...
                  </td>
                </tr>
              )}
              {!loading && categories.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-center py-8 text-slate-500">
                    No categories found
                  </td>
                </tr>
              )}
              {categories.map((cat) => (
                <tr
                  key={cat._id}
                  className="border-b border-slate-100 hover:bg-slate-50"
                >
                  <td className="py-3 px-4">
                    <img
                      src={cat.image}
                      className="w-12 h-12 rounded-lg object-cover"
                      alt={cat.name}
                    />
                  </td>
                  <td className="py-3 px-4 font-medium text-slate-800">
                    {cat.name}
                  </td>
                  <td className="py-3 px-4 text-slate-600 max-w-xs truncate">
                    {cat.description || "-"}
                  </td>
                  <td className="py-3 px-4 text-slate-600">{cat.order || 0}</td>
                  <td className="py-3 px-4">
                    <div className="flex gap-2">
                      <button
                        onClick={() => openEditModal(cat)}
                        className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(cat._id, cat.name)}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal for Create/Edit */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-slate-800">
                {editingCategory ? "Edit Category" : "Create Category"}
              </h2>
              <button
                onClick={resetForm}
                className="p-1 hover:bg-slate-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Category Name
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="e.g., Funny, Romantic, Islamic"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Description (Optional)
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  rows={2}
                  placeholder="Brief description of the category"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Order
                </label>
                <input
                  type="number"
                  value={order}
                  onChange={(e) => setOrder(parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  placeholder="Display order (lower numbers appear first)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Category Image
                </label>
                <div className="flex items-start gap-4">
                  <label className="cursor-pointer flex-1">
                    <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 text-center hover:border-indigo-400 transition-colors">
                      <Image className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                      <p className="text-sm text-slate-600">Click to upload</p>
                      <p className="text-xs text-slate-400">
                        PNG, JPG, WEBP up to 200KB
                      </p>
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                  </label>
                  {imagePreview && (
                    <div className="relative">
                      <img
                        src={imagePreview}
                        className="w-20 h-20 rounded-lg object-cover border"
                        alt="Preview"
                      />
                      <button
                        onClick={() => {
                          setImagePreview("");
                          setImageBase64("");
                        }}
                        className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={resetForm}
                className="flex-1 px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                {loading ? "Saving..." : editingCategory ? "Update" : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SurpriseCategories;
